import { FC, useEffect, useRef, useState } from 'react';

import { FlightDirectionFilter } from '../../generated/graphql';

export interface FlightRosterDateFilterValue {
  datesFrom?: string;
  datesTo?: string;
  datesDirection: FlightDirectionFilter;
}

interface FlightRosterDateFilterChipProps {
  value: FlightRosterDateFilterValue;
  isEditing: boolean;
  onEditingChange: (editing: boolean) => void;
  onUpdate: (value: FlightRosterDateFilterValue) => void;
}

const directions = [
  { value: FlightDirectionFilter.Either, label: 'Either' },
  { value: FlightDirectionFilter.Outbound, label: 'Outbound' },
  { value: FlightDirectionFilter.Inbound, label: 'Inbound' },
];

const displayDate = (value?: string) => value || 'Any';

const FlightRosterDateFilterChip: FC<FlightRosterDateFilterChipProps> = ({
  value,
  isEditing,
  onEditingChange,
  onUpdate,
}) => {
  const [localValue, setLocalValue] = useState(value);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current?.contains(event.target as Node)) return;
      onEditingChange(false);
    };

    if (isEditing) document.addEventListener('mousedown', handleClickOutside);

    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isEditing, onEditingChange]);

  const directionLabel =
    directions.find(
      (direction) => direction.value === localValue.datesDirection,
    )?.label ?? 'Either';
  const summary = `${displayDate(localValue.datesFrom)} -> ${displayDate(
    localValue.datesTo,
  )} / ${directionLabel}`;

  return (
    <div className="relative" ref={containerRef}>
      <button
        type="button"
        onClick={() => onEditingChange(!isEditing)}
        className="inline-flex items-center gap-x-1 rounded-xl border border-gray-200 bg-lightGreen px-3 py-1 text-sm hover:bg-lightGreen"
      >
        <span className="font-medium">Dates:</span>
        <span className="max-w-44 truncate">{summary}</span>
      </button>

      {isEditing && (
        <div className="absolute left-0 top-full z-10 mt-2 w-72 rounded-xl border border-gray-200 bg-white p-4 shadow-lg">
          <div className="flex flex-col gap-y-4">
            <div className="grid grid-cols-3 gap-1 rounded-lg border border-gray-200 p-1">
              {directions.map((direction) => (
                <button
                  key={direction.value}
                  type="button"
                  aria-pressed={localValue.datesDirection === direction.value}
                  onClick={() =>
                    setLocalValue((current) => ({
                      ...current,
                      datesDirection: direction.value,
                    }))
                  }
                  className="rounded-md px-2 py-1 text-xs font-medium aria-pressed:bg-secondaryBlue aria-pressed:text-white"
                >
                  {direction.label}
                </button>
              ))}
            </div>

            <label className="text-sm font-medium text-gray-700">
              From date
              <input
                aria-label="From date"
                type="date"
                value={localValue.datesFrom ?? ''}
                onChange={(event) =>
                  setLocalValue((current) => ({
                    ...current,
                    datesFrom: event.target.value || undefined,
                  }))
                }
                className="mt-1 block w-full rounded-md border-gray-300 text-sm shadow-sm"
              />
            </label>

            <label className="text-sm font-medium text-gray-700">
              To date
              <input
                aria-label="To date"
                type="date"
                value={localValue.datesTo ?? ''}
                onChange={(event) =>
                  setLocalValue((current) => ({
                    ...current,
                    datesTo: event.target.value || undefined,
                  }))
                }
                className="mt-1 block w-full rounded-md border-gray-300 text-sm shadow-sm"
              />
            </label>

            <div className="flex justify-between">
              <button
                type="button"
                onClick={() =>
                  setLocalValue({
                    datesFrom: undefined,
                    datesTo: undefined,
                    datesDirection: FlightDirectionFilter.Either,
                  })
                }
                className="rounded-md bg-gray-200 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-300"
              >
                Clear
              </button>
              <button
                type="button"
                onClick={() => {
                  onUpdate(localValue);
                  onEditingChange(false);
                }}
                className="rounded-md bg-secondaryBlue px-4 py-2 text-sm font-medium text-white hover:bg-secondaryBlueShade"
              >
                Update
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FlightRosterDateFilterChip;
